import { browser } from '$app/env';
import { PUBLIC_SUPABASE_ANON_KEY, PUBLIC_SUPABASE_URL } from '$app/env/public';
import { createBrowserClient, createServerClient, isBrowser } from '@supabase/ssr';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/svelte-query';
import type { LayoutLoad } from './$types';
import { isApiError } from '~/types';
import { dev } from '$app/env';
import { signedOutModal } from '~/auth/SignedOutModal.svelte';
import { signedOut } from '~/auth/AccountModal.svelte';
import { premiumQueryKey } from '~/lib/queries.svelte';
import { getPremium } from '~/lib/premium.remote.js';
// Disable SSR when running the dev server
export const ssr = !dev;
// export const prerender = !dev;

// Singleton in the browser: reusing the same QueryClient across layout re-runs
// (triggered by invalidate('supabase:auth')) preserves the in-memory cache and
// prevents PersistQueryClientProvider from re-entering isRestoring=true on every
// auth state change.
let browserClient: QueryClient | undefined;

export const load: LayoutLoad = async ({ fetch, data, depends }) => {
  depends('supabase:auth');

  const supabase = isBrowser()
    ? createBrowserClient(PUBLIC_SUPABASE_URL, PUBLIC_SUPABASE_ANON_KEY, {
        global: {
          fetch,
        },
        auth: {
          detectSessionInUrl: false,
        },
      })
    : createServerClient(PUBLIC_SUPABASE_URL, PUBLIC_SUPABASE_ANON_KEY, {
        global: {
          fetch,
        },
        cookies: {
          getAll: () => data?.cookies ?? [],
        },
      });

  const onError = async (err: Error) => {
    if (isApiError(err) && [401, 403].includes(err.status)) {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['stripe'] }),
        // refetchType: 'none' marks as stale without triggering a refetch —
        // prevents a loop where the 401 causes a re-fetch that causes another 401.
        queryClient.invalidateQueries({ queryKey: ['auth'], refetchType: 'none' }),
      ]);
      // TODO
      if (!signedOut.current) {
        signedOutModal.push();
      }
      // clean up session state and prompt for login
      // ex: window.location.reload();
    }
  };

  const retry = (failureCount: number, err: Error) => {
    if (isApiError(err) && [401, 403, 404].includes(err.status)) {
      return false;
    }
    // otherwise, restore default
    const defaultRetry = 1;
    return Number.isSafeInteger(defaultRetry)
      ? failureCount < defaultRetry
      : false;
  };

  const queryClient = browser && browserClient
    ? browserClient
    : browserClient = new QueryClient({
      defaultOptions: {
        queries: {
          enabled: browser,
          refetchOnMount: false,
          refetchOnWindowFocus: false,
          staleTime: Number.POSITIVE_INFINITY,
            retry,
        },
        mutations: {// retry,
        }
      },
      queryCache: new QueryCache({ onError }),
      mutationCache: new MutationCache({ onError })
    });

  // `data` is null when the server load doesn't run (e.g. the static/SPA
  // Capacitor build with SSR disabled), so read session defensively.
  const session = data?.session ?? null;

  const {
    data: { user },
  } = await supabase.auth.getUser();

  let isPremium = false;
  if (user) {
    const premiumData = await getPremium();
    isPremium = premiumData.premium;
    queryClient.setQueryData(premiumQueryKey(), premiumData);
  } else {
    // clear stale premium data so logged-out users don't see premium features
    queryClient.setQueryData(premiumQueryKey(), { premium: false });
  }

  // computed by the server load; false in the static build, where it doesn't run
  const isAdmin = !!user && !!data?.isAdmin;

  return { supabase, user, session, queryClient, isPremium, isAdmin };
};
